import { ChangeDetectorRef, Component, Input, OnChanges, OnInit, OnDestroy, SimpleChanges, ViewChild } from '@angular/core';
import Highcharts from 'highcharts';
import { HighchartsChartComponent } from 'highcharts-angular';
import { UiStateStorageService } from 'app/shared/services/ui-state-storage/ui-state-storage.service';

@Component({
  selector: 'treemap-chart',
  templateUrl: 'treemap.component.html',
  styleUrls: ['./treemap.component.scss'],
  standalone: false
})
export class TreemapComponent implements OnChanges, OnInit, OnDestroy {
  @Input() data!: { name: string; value: number; colorValue: number }[]; // Input for the dataset
  @Input() stateKey = 'treemap'; // Unique identifier for this treemap instance
  private _myData: any[] = []; // Local variable to hold the data

  @ViewChild('treemapchart', { static: false }) treemapchart!: HighchartsChartComponent;
  Highcharts: typeof Highcharts = Highcharts;
  chartOptions: Highcharts.Options = {};
  chartInstance?: Highcharts.Chart;
  private isRestoringState = false;

  constructor(
    private readonly _cdref: ChangeDetectorRef,
    private readonly uiStateService: UiStateStorageService
  ) {}

  private renderChart(): Highcharts.Options {
    return {
      lang: {
        decimalPoint: '.',
        thousandsSep: ','
      },
      title: {
        text: ''
      },
      colorAxis: {
        min: -1,
        max: 1,
        stops: [
          [0.0, '#B23535'],
          [0.1, '#C84343'],
          [0.2, '#D94F4F'],
          [0.4, '#E06666'],
          [0.47, '#E68585'],
          [0.48, '#75787F'],
          [0.5, '#A0A2A7'],
          [0.52, '#7A7D84'],
          [0.53, '#4E8B5B'],
          [0.6, '#52A158'],
          [0.8, '#56B15C'],
          [0.9, '#49DD5B'],
          [1.0, '#41E35B']
        ],
        tickPositions: [-1.0, -0.5, 0.0, 0.5, 1.0]
      },
      legend: {
        align: 'center',
        layout: 'horizontal',
        verticalAlign: 'bottom',
        symbolWidth: 200
      },
      exporting: {
        enabled: false
      },
      credits: {
        enabled: false
      },
      chart: {
        events: {
          render: function () {
            // 'this' is the chart instance
            const hasData = this.series && this.series[0] && this.series[0].data && this.series[0].data.length > 0;
            if (!hasData) {
              this.showLoading('Loading chart...');
            } else {
              this.hideLoading();
            }
          }
        }
      },
      series: [
        {
          states: {
            hover: {
              enabled: true,
              lineWidth: 5
            }
          },
          name: 'All',
          type: 'treemap',
          layoutAlgorithm: 'squarified',
          allowTraversingTree: true,
          nodeSizeBy: 'leaf',
          events: {
            click: (e: any) => {
              if (!this.isRestoringState && e.point) {
                // Save the current drilldown level after click
                setTimeout(() => {
                  this.saveDrilldownState();
                }, 100);
              }
            }
          },
          point: {
            events: {
              click: (e: any) => {
                if (!this.isRestoringState) {
                  // Save state after point is clicked and drilldown happens
                  setTimeout(() => {
                    this.saveDrilldownState();
                  }, 100);
                }
              }
            }
          },
          dataLabels: {
            enabled: true,
            style: {
              fontSize: '0.9em',
              textOutline: 'none'
            },
            formatter: function (this, options) {
              const width = this.shapeArgs?.['width'] || 0;
              const height = this.shapeArgs?.['height'] || 0;
              if (width < 65 || height < 20) {
                return '';
              }
              return this.name;
            }
          },
          levels: [
            {
              level: 1,
              dataLabels: {
                enabled: true,
                headers: true,
                crop: true,
                verticalAlign: 'top',
                align: 'center',
                backgroundColor: 'white',
                inside: true,
                allowOverlap: false,
                style: {
                  fontWeight: 'bold',
                  color: 'black',
                  textTransform: 'uppercase',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  fontSize: '10px'
                }
              },
              borderColor: 'white',
              borderWidth: 1
            },
            {
              level: 2,
              dataLabels: {
                enabled: true,
                headers: true,
                crop: true,
                inside: true,
                style: {
                  color: 'white',
                  fontWeight: 'normal',
                  fontSize: '0.6em',
                  textTransform: 'uppercase'
                }
              }
            }
          ],
          breadcrumbs: {
            separator: { text: '>' },
            buttonTheme: {
              style: {
                color: '#52667a',
                cursor: 'pointer'
              },
              states: {
                select: {
                  style: {
                    color: '#52667a'
                  }
                }
              }
            },
            events: {
              click: (e: any) => {
                if (!this.isRestoringState) {
                  // Save state after breadcrumb click
                  setTimeout(() => {
                    this.saveDrilldownState();
                  }, 100);
                }
              }
            }
          },
          data: this._myData
        }
      ],
      tooltip: {
        headerFormat: '<b style="color:#52667a">{point.parent}</b><br/><br/>',
        pointFormat: '<b>{point.name}</b><br/><p style="color:#52667a">{point.value:,.2f}</p><br/>',
        borderRadius: 12
      }
    };
  }

  ngOnInit(): void {
    this.updateChartData();
  }

  onChartInstance = (chart: Highcharts.Chart) => {
    this.chartInstance = chart;
    if (this._myData && this._myData.length > 0) {
      chart.hideLoading();
      // Restore drilldown state after chart is fully initialized and rendered
      setTimeout(() => {
        this.restoreDrilldownState();
      }, 500);
    } else {
      chart.showLoading('Loading chart...');
    }
  };

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['data']) {
      this._myData = this.data ? [...this.data] : [];
      this.updateChartData();

      // After data changes and chart updates, restore drilldown state
      if (this.chartInstance && this._myData.length > 0) {
        setTimeout(() => {
          this.restoreDrilldownState();
        }, 500);
      }
    }
  }

  private updateChartData(): void {
    if (this.data) {
      this.chartOptions = this.renderChart(); // Call the drawChart method to get the updated options
    }
  }

  private saveDrilldownState(): void {
    if (!this.chartInstance || !this.chartInstance.series) return;

    const series = this.chartInstance.series[0] as any;
    if (!series) return;

    // Get the current root node ID from the series
    const currentRootId = series.rootNode;

    if (currentRootId && currentRootId !== '' && currentRootId !== series.tree?.id) {
      // We're in a drilled state - save the current root node ID
      this.uiStateService.saveCustomState(this.stateKey, { drilldownId: currentRootId });
    } else {
      // We're at the top level - clear the saved state
      this.uiStateService.clearPageState(this.stateKey);
    }
  }

  private restoreDrilldownState(): void {
    const customState = this.uiStateService.getCustomState(this.stateKey);
    const savedDrilldownId = customState?.['drilldownId'];

    if (savedDrilldownId && this.chartInstance) {
      this.isRestoringState = true;
      const series = this.chartInstance.series[0] as any;

      if (series && series.setRootNode) {
        try {
          series.setRootNode(savedDrilldownId);
        } catch (error) {
          console.error('Error restoring treemap drilldown state:', error);
          this.uiStateService.clearPageState(this.stateKey);
        }
      }

      setTimeout(() => {
        this.isRestoringState = false;
      }, 200);
    }
  }

  ngOnDestroy(): void {
    // Save current state before component is destroyed
    this.saveDrilldownState();
  }
}
